import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Printer, FileText, Download, Trash2, Upload } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import PrintLayout from '@/components/PrintLayout';
import { prepareDocumentsForPrint } from '@/lib/documentRenderer';
import api, { formatApiError } from '@/lib/api';

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground w-44 shrink-0">{label}</span>
      <span className="text-sm">{value || '-'}</span>
    </div>
  );
}

export default function ClientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [companyName, setCompanyName] = useState('K. G. Patel & Company');
  const [documentPages, setDocumentPages] = useState([]);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const printRef = useRef();

  const fetchClient = useCallback(() => {
    api.get(`/clients/${id}`)
      .then((r) => setClient(r.data))
      .catch(() => toast.error('Failed to load client'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchClient(); }, [fetchClient]);
  useEffect(() => { api.get('/settings').then((r) => setCompanyName(r.data.company_name || 'K. G. Patel & Company')).catch(() => {}); }, []);

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: `${client?.trust_name || 'Client'} - ${client?.trust_id || 'Profile'}` });

  const handlePrintClick = async () => {
    if (!client) return;
    const docs = client.documents || [];
    if (docs.length === 0) {
      handlePrint();
      return;
    }

    setPreparingPrint(true);
    toast.info('Preparing documents for print... This may take a moment.');
    try {
      const pages = await prepareDocumentsForPrint(docs);
      setDocumentPages(pages);
      // Wait for React to render the images in PrintLayout
      await new Promise((r) => setTimeout(r, 500));
      handlePrint();
    } catch (err) {
      console.error('Print preparation failed:', err);
      toast.error('Failed to prepare some documents. Printing without document previews.');
      handlePrint();
    }
    setPreparingPrint(false);
  };

  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files);
    if (!fileList.length) return;
    setUploading(true);
    try {
      for (const file of fileList) { const fd = new FormData(); fd.append('file', file); fd.append('client_id', id); await api.post('/documents/upload', fd); }
      toast.success(`${fileList.length} file(s) uploaded`);
      fetchClient();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete(`/documents/${docId}`); toast.success('Document deleted'); fetchClient(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!client) return <p className="text-center py-20 text-muted-foreground">Client not found</p>;

  const sectionsOrder = client.sections_order?.length ? client.sections_order : ['trust_details', 'registration_details', 'udin_details', 'bank_details'];

  const renderSection = (sid) => {
    if (sid === 'trust_details') return (
      <Card key={sid}><CardContent className="p-5">
        <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('trust_details')}</h2>
        <div className="divide-y">
          <InfoRow label={t('trust_name')} value={client.trust_name} />
          <InfoRow label={t('trust_number')} value={client.trust_number} />
          <InfoRow label={t('trust_pan')} value={client.trust_pan} />
          <InfoRow label={t('trustee_name')} value={client.trustee_name} />
          <InfoRow label={t('trustee_number')} value={client.trustee_number} />
          <InfoRow label={t('email')} value={client.email} />
          <InfoRow label={t('phone')} value={client.phone} />
          <InfoRow label={t('category')} value={client.category} />
          <InfoRow label={t('financial_year')} value={client.financial_year} />
          <InfoRow label={t('address')} value={client.address} />
        </div>
      </CardContent></Card>
    );
    if (sid === 'registration_details') return (
      <Card key={sid}><CardContent className="p-5">
        <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('registration_details')}</h2>
        <div className="divide-y">
          <InfoRow label={t('registration_12a')} value={client.registration_12a} />
          <InfoRow label={t('registration_80g')} value={client.registration_80g} />
          <InfoRow label="12A Due Date" value={client.due_date_12a ? new Date(client.due_date_12a).toLocaleDateString() : '-'} />
          <InfoRow label="80G Due Date" value={client.due_date_80g ? new Date(client.due_date_80g).toLocaleDateString() : '-'} />
        </div>
      </CardContent></Card>
    );
    if (sid === 'udin_details') return (
      <Card key={sid}><CardContent className="p-5">
        <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>UDIN Registration</h2>
        <div className="divide-y">
          <InfoRow label="UDIN Number" value={client.udin_number} />
          <InfoRow label="Year" value={client.udin_year} />
        </div>
      </CardContent></Card>
    );
    if (sid === 'bank_details') return (
      <Card key={sid}><CardContent className="p-5">
        <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('bank_details')}</h2>
        <div className="divide-y">
          <InfoRow label={t('bank_account')} value={client.bank_account} />
          <InfoRow label={t('bank_ifsc')} value={client.bank_ifsc} />
          <InfoRow label={t('bank_name')} value={client.bank_name} />
        </div>
      </CardContent></Card>
    );
    if (sid === 'documents') return null;
    const cs = client.custom_sections?.find((s) => s.id === sid);
    if (!cs) return null;
    return (
      <Card key={sid}><CardContent className="p-5">
        <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{cs.section_name}</h2>
        <div className="divide-y">
          {cs.fields.map((f) => <InfoRow key={f.id} label={f.label} value={f.type === 'checkbox' ? (f.value ? 'Yes' : 'No') : (f.value || '-')} />)}
          {cs.fields.length === 0 && <p className="text-sm text-muted-foreground py-2">No fields in this section</p>}
        </div>
      </CardContent></Card>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="client-profile-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clients')} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{client.trust_name || 'Unnamed Trust'}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-mono text-muted-foreground">{client.trust_id}</span>
              <Badge className={client.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}>
                {client.status === 'active' ? t('active') : t('inactive')}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrintClick} disabled={preparingPrint} data-testid="print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" />{preparingPrint ? 'Preparing...' : 'Print / PDF'}
          </Button>
          <Button size="sm" onClick={() => navigate(`/clients/${id}/edit`)} data-testid="edit-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" />{t('edit')}</Button>
        </div>
      </div>

      {sectionsOrder.map((sid) => renderSection(sid))}

      {/* Documents */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('documents')}</h2>
            <Button variant="outline" size="sm" onClick={() => document.getElementById('profile-file-upload').click()} disabled={uploading} data-testid="upload-doc-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" />{uploading ? 'Uploading...' : t('upload_documents')}
            </Button>
            <input id="profile-file-upload" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv" className="hidden" onChange={handleUpload} />
          </div>
          {client.documents?.length > 0 ? (
            <div className="space-y-2">
              {client.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-muted/40 rounded-lg px-4 py-3" data-testid={`doc-${doc.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                      <p className="text-xs text-muted-foreground">{doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''} &middot; {doc.uploaded_by_name || 'Unknown'}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(doc)}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground text-center py-8">{t('no_data')}</p>}
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card><CardContent className="p-5">
        <h2 className="text-base font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Metadata</h2>
        <div className="divide-y">
          <InfoRow label="Created By" value={client.created_by_name} />
          <InfoRow label="Created At" value={client.created_at ? new Date(client.created_at).toLocaleString() : '-'} />
          <InfoRow label="Updated At" value={client.updated_at ? new Date(client.updated_at).toLocaleString() : '-'} />
        </div>
      </CardContent></Card>

      {/* Hidden Print Layout - includes rendered document pages */}
      <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
        <PrintLayout
          ref={printRef}
          client={client}
          companyName={companyName}
          userName={user?.name}
          documentPages={documentPages}
        />
      </div>
    </div>
  );
}
